/**
 * FlexpayService.js
 * Service for handling Flexpay payment integration
 */

import config from '../configuration/config';
import { formatPackageTitle } from '../helpers/TripHelper';

/**
 * Convert full state name to 2-letter abbreviation
 * @param {string} stateName - Full state name (e.g. 'Washington')
 * @returns {string} 2-letter state abbreviation or original string if not found
 */
const getStateAbbreviation = stateName => {
  const stateAbbreviations = {
    Alabama: 'AL',
    Alaska: 'AK',
    Arizona: 'AZ',
    Arkansas: 'AR',
    California: 'CA',
    Colorado: 'CO',
    Connecticut: 'CT',
    Delaware: 'DE',
    Florida: 'FL',
    Georgia: 'GA',
    Hawaii: 'HI',
    Idaho: 'ID',
    Illinois: 'IL',
    Indiana: 'IN',
    Iowa: 'IA',
    Kansas: 'KS',
    Kentucky: 'KY',
    Louisiana: 'LA',
    Maine: 'ME',
    Maryland: 'MD',
    Massachusetts: 'MA',
    Michigan: 'MI',
    Minnesota: 'MN',
    Mississippi: 'MS',
    Missouri: 'MO',
    Montana: 'MT',
    Nebraska: 'NE',
    Nevada: 'NV',
    NewHampshire: 'NH',
    NewJersey: 'NJ',
    NewMexico: 'NM',
    NewYork: 'NY',
    NorthCarolina: 'NC',
    NorthDakota: 'ND',
    Ohio: 'OH',
    Oklahoma: 'OK',
    Oregon: 'OR',
    Pennsylvania: 'PA',
    RhodeIsland: 'RI',
    SouthCarolina: 'SC',
    SouthDakota: 'SD',
    Tennessee: 'TN',
    Texas: 'TX',
    Utah: 'UT',
    Vermont: 'VT',
    Virginia: 'VA',
    Washington: 'WA',
    WestVirginia: 'WV',
    Wisconsin: 'WI',
    Wyoming: 'WY',
    DistrictofColumbia: 'DC',
    PuertoRico: 'PR',
    Alberta: 'AB',
    BritishColumbia: 'BC',
    Manitoba: 'MB',
    NewBrunswick: 'NB',
    NewfoundlandandLabrador: 'NL',
    NorthwestTerritories: 'NT',
    NovaScotia: 'NS',
    Nunavut: 'NU',
    Ontario: 'ON',
    PrinceEdwardIsland: 'PE',
    Quebec: 'QC',
    Saskatchewan: 'SK',
    Yukon: 'YT',
  };

  const normalizedState = stateName ? stateName.trim() : '';
  return stateAbbreviations[normalizedState] || normalizedState;
};

/**
 * Check if Flexpay is enabled in the current environment
 */
export const isFlexpayEnabled = () => {
  return config.flexpay && config.flexpay.upCode && config.flexpay.active;
};

export const getFlexpayLocaleCurrency = ({ currency } = {}) => {
  if (currency === 'CAD') {
    return { locale: 'en-CA', currency: 'CAD' };
  }

  return { locale: 'en-US', currency: 'USD' };
};

export const getMerchantIdForCurrency = currency => {
  return currency === 'CAD' && config.flexpay.upCodeCanada
    ? config.flexpay.upCodeCanada
    : config.flexpay.upCode;
};

/**
 * Initialize Flexpay with the configuration from the environment
 * @param {Function} callback - Callback function to handle Uplift payment events
 * @param {Object} options - Options for initialization
 * @param {string} options.currency - Currency code (e.g. 'USD', 'CAD')
 * @returns {boolean} - Whether initialization was successful
 */
export const initializeFlexpay = (callback, { currency } = {}) => {
  if (!isFlexpayEnabled()) {
    console.warn('[FlexpayService] Flexpay is not enabled');
    return false;
  }

  // Determine which Flexpay ID to use based on currency
  const flexpayId =
    currency === 'CAD' && config.flexpay.upCodeCanada
      ? config.flexpay.upCodeCanada
      : config.flexpay.upCode;

  // Store the callback for later use
  window.upliftPaymentCallback = callback;

  // Initialize Uplift script
  (function(u, p, l, i, f, t, o, b, j) {
    // Set up the Uplift platform object
    u['UpLiftPlatformObject'] = f;

    // Create the up function queue
    u[f] =
      u[f] ||
      function() {
        (u[f].q = u[f].q || []).push(arguments);
      };
    u[f].l = 1 * new Date();

    // Create script element
    b = p.createElement(l);
    j = p.getElementsByTagName(l)[0];
    b.async = 1;
    b.src = i + '?upcode=' + t;

    if (j && j.parentNode) {
      j.parentNode.insertBefore(b, j);
    } else {
      p.head.appendChild(b);
    }
    o = u.location.host.match(/[\w-]+\.\w{2,3}(:\d+)?$/);
    if (o) o = o[0];
    u[f]('create', t, o);
  })(window, document, 'script', config.flexpay.upUrl, 'up', flexpayId);

  return true;
};

/**
 * Initialize Uplift Payments with the given configuration
 * @param {string} containerId - ID of the container element for Uplift
 * @returns {void}
 */
// Track if Uplift Payments has been initialized
export const initUpliftPayments = (
  containerId = '#up-pay-monthly-container',
  customerId = '',
  { currency } = {}
) => {
  // Check if already initialized with the same container and customer
  if (window.upliftPaymentsInitialized) {
    console.warn('[FlexpayService] Uplift Payments already initialized, skipping');
    return;
  }

  if (window.Uplift && window.Uplift.Payments) {
    try {
      const { locale, currency: upliftCurrency } = getFlexpayLocaleCurrency({ currency });

      window.Uplift.Payments.init({
        locale,
        currency: upliftCurrency,
        checkout: true,
        channel: 'desktop',
        container: containerId,
        onChange: function(data) {
          // Call the stored callback method
          if (window.upliftPaymentCallback) {
            window.upliftPaymentCallback(data);
          }
        },
      });

      // Mark as initialized
      window.upliftPaymentsInitialized = true;
    } catch (error) {
      console.error('Error initializing Uplift Payments:', error);
    }
  } else {
    console.error('Uplift.Payments is not available');
  }
};

export const resetUpliftPayments = () => {
  try {
    window.upliftPaymentsInitialized = false;

    if (
      window.Uplift &&
      window.Uplift.Payments &&
      typeof window.Uplift.Payments.unmount === 'function'
    ) {
      window.Uplift.Payments.unmount();
    }
  } catch (error) {
    window.upliftPaymentsInitialized = false;
  }
};

/**
 * Format date string as YYYYMMDD for Flexpay
 * @param {string} dateStr - Date string to format
 * @returns {string|null} Formatted date string or null if invalid
 */
export const formatTravelDate = dateStr => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}${month}${day}`;
  } catch (e) {
    console.error('Error formatting date for Flexpay:', e);
    return null;
  }
};

/**
 * Format date of birth if available
 * @param {string} dobStr - Date of birth string
 * @returns {string} Formatted date of birth or empty string if invalid
 */
export const formatDOB = dateStr => {
  if (!dateStr) return null;

  // Handle MM/DD/YYYY format
  const mmddyyyyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mmddyyyyMatch) {
    const [, month, day, year] = mmddyyyyMatch;
    return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
  }

  // Handle YYYY-MM-DD format
  const yyyymmddMatch = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (yyyymmddMatch) {
    const [, year, month, day] = yyyymmddMatch;
    return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
  }

  return null;
};

/**
 * Format phone number to be a 10-digit number with no special characters
 * @param {string} phoneStr - Phone number string to format
 * @returns {string} Formatted phone number or empty string if invalid
 */
export const formatPhoneNumber = phoneStr => {
  if (!phoneStr) return '';

  // Remove all non-digit characters
  const digitsOnly = phoneStr.replace(/\D/g, '');

  // If we have exactly 10 digits, return them
  if (digitsOnly.length === 10) {
    return digitsOnly;
  }

  // If we have 11 digits and it starts with 1 (US country code), remove the 1
  if (digitsOnly.length === 11 && digitsOnly.charAt(0) === '1') {
    return digitsOnly.substring(1);
  }

  // Return whatever digits we have, or empty string if no valid format
  return digitsOnly.length > 0 ? digitsOnly.substring(0, 10) : '';
};

/**
 * Convert dollar amount to cents for Flexpay
 * @param {number} amount - Amount in dollars
 * @returns {number} Amount in cents
 */
export const toCent = amount => {
  return Math.round(amount * 100);
};

/**
 * Create order info object for Flexpay
 * @param {Object} params - Parameters for creating order info
 * @param {Object} params.trip - Trip object
 * @param {Object} params.user - User object
 * @param {number} params.totalAmountDue - Total amount due in dollars
 * @param {string} [params.currency] - Currency code (e.g. 'USD', 'CAD')
 * @returns {Object} Order info object for Flexpay
 */
export const createOrderInfo = ({ trip, user, totalAmountDue, currency }) => {
  let tripName = formatPackageTitle(trip.destination, trip.currentAddOns);
  // Calculate total amount in cents
  const totalAmountInCents = toCent(totalAmountDue);

  // Build add-ons array and calculate total in one pass
  const addOns = [];
  let addOnTotal = 0;

  if (trip && trip.currentAddOns && trip.currentAddOns.length > 0) {
    trip.currentAddOns.forEach(addon => {
      const addonPrice = toCent(addon.unitPrice || 0);
      addOnTotal += addonPrice;

      addOns.push({
        id: addon.id,
        name: addon.name || 'Trip Add-on',
        types: ['travel'],
        price: addonPrice,
      });
    });
  }

  // Calculate main trip price (total minus add-ons)
  let mainTripPrice = totalAmountInCents - addOnTotal;

  // Handle case where mainTripPrice is negative (main trip is paid off)
  if (mainTripPrice < 0 && addOns.length > 0) {
    // Start with the negative balance (credit) from main trip
    let ledgerBalance = mainTripPrice;
    mainTripPrice = 0; // Set main trip price to zero since it's paid off

    // Apply the credit to add-ons in order
    for (let i = 0; i < addOns.length && ledgerBalance < 0; i++) {
      // Calculate how much we can apply to this add-on
      const creditToApply = Math.max(ledgerBalance, -addOns[i].price);
      // Apply the credit to the add-on
      addOns[i].price += creditToApply;
      // Update the remaining ledger balance
      ledgerBalance -= creditToApply;
    }

    // If there's still negative balance after applying to all add-ons,
    // we have an overpayment situation - this shouldn't normally happen
    if (ledgerBalance < 0) {
      console.warn('Overpayment detected after applying credits to all items', { ledgerBalance });
    }
  }

  let orderObject = {
    travelers: [
      {
        id: 0,
        first_name: user.firstName || '',
        last_name: user.lastName || '',
      },
    ],
    billing_contact: {
      first_name: user.firstName,
      last_name: user.lastName,
      street_address: user.mailingStreet || '',
      city: user.mailingCity || '',
      region: getStateAbbreviation(user.mailingState) || '',
      country:
        user.mailingCountry === 'United States'
          ? 'US'
          : user.mailingCountry === 'Canada'
          ? 'CA'
          : '',
      postal_code: user.mailingPostalCode || '',
      date_of_birth: formatDOB(user.birthdate) || '',
      phone: formatPhoneNumber(user.phone) || '',
      email: user.email || '',
    },
    address: {
      street_address: user.mailingStreet || '',
      city: user.mailingCity || '',
      region: getStateAbbreviation(user.mailingState) || '',
      country:
        user.mailingCountry === 'United States'
          ? 'US'
          : user.mailingCountry === 'Canada'
          ? 'CA'
          : '',
      postal_code: user.mailingPostalCode || '',
    },
    event_tickets: [
      {
        reference_id: trip.id,
        event_name: tripName,
        venue_name: trip
          ? trip.destination || 'International Destination'
          : 'International Destination',
        type: 'trip',
        unit_price: mainTripPrice,
        quantity: 1,
        start_date: formatTravelDate(trip ? trip.startDate : null),
        end_date: formatTravelDate(trip ? trip.endDate : null),
        insurance: [],
      },
    ],
    add_ons: addOns,
    order_amount: toCent(totalAmountDue),
  };

  return orderObject;
};

/**
 * Load order info into Flexpay
 * @param {Object} orderInfo - Order info object
 * @returns {void}
 */
export const loadOrderInfo = orderInfo => {
  if (window.Uplift && window.Uplift.Payments) {
    window.Uplift.Payments.load(orderInfo);
  } else {
    console.error('Uplift.Payments is not available for loading order info');
  }
};

export default {
  isFlexpayEnabled,
  getFlexpayLocaleCurrency,
  getMerchantIdForCurrency,
  initializeFlexpay,
  initUpliftPayments,
  resetUpliftPayments,
  formatTravelDate,
  formatDOB,
  toCent,
  createOrderInfo,
  loadOrderInfo,
};
